// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useCallback, useState } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAskHelios, SITE_SUGGESTED_QUESTIONS } from '../../hooks/useAskHelios';
import ChatInput from './ChatInput';
import ConversationHistory from './ConversationHistory';
import { useShellStore } from '../../stores/shellStore';
import { NO_SITE_WELCOME } from '../../stores/chatStore';
import { hasSelectedSite } from '../../hooks/useActiveSite';
import type { AskHeliosCitation, AskHeliosMessage } from '../../types';
import CitationChip from './CitationChip';
import './AskHeliosChat.css';

const CONFIDENCE_LABEL: Record<string, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
};

// Marker set mirrors site_ops_agent/workflow.py, adapter.py::_CITATION_RE.
// The BFF strips a Manual passage id ("[Manual M1]"); the suffix is accepted in case one leaks.
const INLINE_CITATION_RE = /\[(?:(Alarm|SOP|KG|Telemetry)|(Manual)(?: M\d+)?)\]/g;

export interface MdastNode {
  type: string;
  value?: string;
  children?: MdastNode[];
  [key: string]: unknown;
}

function splitCitationText(value: string): MdastNode[] {
  const parts: MdastNode[] = [];
  let lastIndex = 0;
  INLINE_CITATION_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = INLINE_CITATION_RE.exec(value))) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: value.slice(lastIndex, match.index) });
    }
    const block = match[1] ?? match[2];
    parts.push({
      type: 'link',
      url: `cite:${block}`,
      children: [{ type: 'text', value: block }],
    });
    lastIndex = match.index + match[0].length;
  }
  if (!parts.length) return [{ type: 'text', value }];
  if (lastIndex < value.length) {
    parts.push({ type: 'text', value: value.slice(lastIndex) });
  }
  return parts;
}

function transformChildren(node: MdastNode): void {
  if (!Array.isArray(node.children)) return;
  const inLink = node.type === 'link' || node.type === 'linkReference';
  const next: MdastNode[] = [];
  for (const child of node.children) {
    if (child.type === 'text' && !inLink) {
      next.push(...splitCitationText(child.value ?? ''));
    } else {
      transformChildren(child);
      next.push(child);
    }
  }
  node.children = next;
}

// Exported for direct unit testing — react-markdown is mocked in Jest (ESM-only,
// see src/__mocks__/react-markdown.tsx), and the mock ignores remarkPlugins, so
// there's no way to exercise this transform through the rendered component.
export function remarkInlineCitations() {
  return (tree: MdastNode) => {
    transformChildren(tree);
  };
}

function citeUrlTransform(url: string): string {
  return url.startsWith('cite:') ? url : defaultUrlTransform(url);
}

export function renderMarkdownLink(
  citations: AskHeliosCitation[] | undefined,
  { href, children, ...rest }: React.ComponentPropsWithoutRef<'a'>
) {
  if (href?.startsWith('cite:')) {
    const ref = href.slice('cite:'.length);
    // Several entries can share a ref (one per manual); any suspect one warns.
    const suspect = !!citations?.some(c => c.ref === ref && c.quality === 'suspect');
    return <CitationChip label={ref} suspect={suspect} />;
  }
  return (
    <a href={href} {...rest}>
      {children}
    </a>
  );
}

/** Human labels for the route the answer came from. */
const ROUTE_LABEL: Record<string, string> = {
  orchestrator: 'Orchestrator',
  'sop-librarian': 'SOP Librarian',
  'knowledge-graph': 'Power Flow',
  'advisory-boundary': 'Advisory boundary',
  refusal: 'Advisory boundary',
};

/** Design colour for the route pill; unmapped routes stay neutral. */
const ROUTE_TONE: Record<string, string> = {
  'sop-librarian': 'ok',
  'knowledge-graph': 'helios',
  'advisory-boundary': 'warn',
  refusal: 'warn',
};

// Display-only: "New-Cummins-DQKAN_Submittal.pdf p.12" -> "Cummins DQKAN Submittal p.12".
const manualDisplayName = (label: string) =>
  label
    .replace(/\.pdf\b/i, '')
    .replace(/^new[-_]/i, '')
    .replace(/[-_\s]+/g, ' ')
    .trim();

// Pills shown per citation type before the rest collapse behind "+N more".
const MAX_CITES_PER_TYPE = 3;

function AssistantMeta({ message }: { message: AskHeliosMessage }) {
  const [expandedTypes, setExpandedTypes] = useState<ReadonlySet<string>>(new Set());
  const toggleType = (type: string) =>
    setExpandedTypes(prev => {
      const next = new Set(prev);
      if (!next.delete(type)) next.add(type);
      return next;
    });
  const showConfidence = !!message.confidence;
  const citations = message.citations ?? [];
  const showCitations = citations.length > 0;
  // Orchestrator badge hidden for now.
  const routeLabel =
    message.route && message.route !== 'orchestrator'
      ? (ROUTE_LABEL[message.route] ?? message.route)
      : null;
  if (!showConfidence && !showCitations) return null;

  return (
    <div className="akh-meta" data-testid="ask-meta">
      {showConfidence && (
        <span className={`akh-conf akh-conf-${message.confidence}`} data-testid="ask-confidence">
          {CONFIDENCE_LABEL[message.confidence as string] ?? message.confidence}
        </span>
      )}
      {showConfidence && routeLabel && (
        <span
          className={`akh-pill akh-route-${ROUTE_TONE[message.route as string] ?? 'neutral'}`}
          data-testid="ask-route"
        >
          {routeLabel}
        </span>
      )}
      {showConfidence && message.abstained && (
        <span className="akh-pill akh-route-warn">
          {message.route === 'sop-librarian' ? 'No approved SOP found' : 'No supporting data found'}
        </span>
      )}
      {citations.length > 0 && (
        <span role="list" aria-label="Sources" className="akh-cite-list">
          {Object.entries(
            citations.reduce<Record<string, AskHeliosCitation[]>>((groups, c) => {
              (groups[c.type] ??= []).push(c);
              return groups;
            }, {})
          ).map(([type, group]) => {
            const extra = group.length - MAX_CITES_PER_TYPE;
            const expanded = expandedTypes.has(type);
            const typeName = type === 'sop' ? 'SOP' : type.charAt(0).toUpperCase() + type.slice(1);
            return (
              <React.Fragment key={type}>
                {(expanded ? group : group.slice(0, MAX_CITES_PER_TYPE)).map((c, i) => (
                  <span
                    key={`${c.ref}-${i}`}
                    role="listitem"
                    className={`akh-pill akh-cite akh-route-${c.quality === 'suspect' ? 'warn' : 'helios'}`}
                    title={`${c.type === 'manual' && c.label ? c.label : c.type}${c.quality === 'suspect' ? ' (unverified)' : ''}`}
                    data-testid="ask-citation"
                  >
                    {c.quality === 'suspect' && (
                      <>
                        <span aria-hidden="true">⚠ </span>
                        <span className="akh-visually-hidden">Unverified source: </span>
                      </>
                    )}
                    {c.label ? (
                      <>
                        <span className="akh-chip-src">{c.ref}:</span>{' '}
                        {c.type === 'manual' ? manualDisplayName(c.label) : c.label}
                      </>
                    ) : (
                      c.ref
                    )}
                  </span>
                ))}
                {extra > 0 && (
                  <span role="listitem" className="akh-cite-more-item">
                    <button
                      type="button"
                      className="akh-pill akh-route-neutral akh-cite-more"
                      aria-expanded={expanded}
                      aria-label={`${expanded ? 'Show fewer' : `Show ${extra} more`} ${typeName} sources`}
                      onClick={() => toggleType(type)}
                    >
                      {expanded ? 'Show less' : `+${extra} more`}
                    </button>
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </span>
      )}
    </div>
  );
}

function ProgressBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="akh-msg akh-msg-assistant">
      {/* Announced by the surrounding role="log" live region. */}
      <div className="akh-progress" data-testid="ask-progress">
        <span className="akh-dots" aria-hidden="true" data-testid="ask-progress-dots">
          <span />
          <span />
          <span />
        </span>
        <span className="akh-progress-text">{children}</span>
      </div>
    </div>
  );
}

interface AskHeliosChatProps {
  /** Whether the conversation-history list is showing instead of the thread. */
  showHistory: boolean;
  /** Owned by the panel header, which also renders the toggle button. */
  setShowHistory: (_show: boolean) => void;
}

const NO_SITE_PLACEHOLDER = 'Select a site to ask anything...';

/**
 * The app's chat surface, rendered inside the Ask Helios slide-over on every
 * screen. Each answer arrives as a complete envelope, rendered with a confidence
 * badge and citation chips.
 *
 * State comes from `stores/chatStore` via `useAskHelios`, so the thread outlives
 * this component — including the "new chat" action, which the panel header owns.
 */
function AskHeliosChat({ showHistory, setShowHistory }: AskHeliosChatProps) {
  const {
    messages,
    inputValue,
    setInputValue,
    isThinking,
    progressLabel,
    conversationId,
    unsaved,
    isLoadingConversation,
    messagesEndRef,
    handleSendMessage,
    handleNewChat,
    stop,
    resumeConversation,
    isEmpty,
    askQuestion,
    retryableQuestion,
    threadSeed,
    contextSeedSent,
    pageAlarmContext,
    attachContext,
    removeContext,
  } = useAskHelios();

  const hasSite = useShellStore(s => hasSelectedSite(s.selectedSiteId));

  const handleResume = useCallback(
    (id: string) => {
      setShowHistory(false);
      void resumeConversation(id);
    },
    [resumeConversation, setShowHistory]
  );

  const lastIndex = messages.length - 1;

  if (showHistory) {
    return (
      <div className="akh-chat">
        <ConversationHistory
          activeConversationId={conversationId}
          onResume={handleResume}
          onArchivedActive={handleNewChat}
        />
      </div>
    );
  }

  return (
    <div className="akh-chat">
      <div className="akh-messages" role="log" aria-live="polite" aria-relevant="additions text">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`akh-msg akh-msg-${m.role}${m.error ? ' akh-msg-error' : ''}${
              m.persisted === false ? ' akh-msg-unsaved' : ''
            }`}
          >
            <div
              className="akh-bubble"
              data-testid={m.role === 'assistant' ? 'ask-answer' : 'ask-question'}
            >
              {m.role === 'assistant' ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkInlineCitations]}
                  urlTransform={citeUrlTransform}
                  components={{ a: props => renderMarkdownLink(m.citations, props) }}
                >
                  {m.welcome && !hasSite ? NO_SITE_WELCOME : m.content}
                </ReactMarkdown>
              ) : (
                m.content
              )}
              {m.role === 'assistant' && <AssistantMeta message={m} />}
              {m.persisted === false && (
                <span className="akh-bubble-unsaved" title="Not saved to history">
                  <span className="akh-unsaved-dot" aria-hidden="true" />
                  <span className="akh-visually-hidden">Not saved to history</span>
                </span>
              )}
            </div>
            {m.error && i === lastIndex && retryableQuestion && !isThinking && (
              <button
                type="button"
                className="akh-retry-btn"
                onClick={() => askQuestion(retryableQuestion)}
              >
                Retry
              </button>
            )}
          </div>
        ))}

        {isLoadingConversation && <ProgressBubble>Loading conversation…</ProgressBubble>}

        {isThinking && <ProgressBubble>{progressLabel ?? 'Working on it…'}</ProgressBubble>}
        <div ref={messagesEndRef} />
      </div>

      {unsaved && !isThinking && (
        <p className="akh-unsaved" role="status">
          <span className="akh-unsaved-dot" aria-hidden="true" />
          Some answers in this thread weren’t saved to history and won’t be available after a
          reload.
        </p>
      )}

      {isEmpty && !isThinking && hasSite && (
        <div className="akh-suggestions" role="group" aria-label="Suggested questions">
          {SITE_SUGGESTED_QUESTIONS.map(q => (
            <button key={q} type="button" className="akh-suggestion" onClick={() => askQuestion(q)}>
              {q}
            </button>
          ))}
        </div>
      )}

      <p className="akh-footer-disclaimer" data-testid="ask-disclaimer">
        EnergyFluo AI can make mistakes. Review its output before acting on it.
      </p>

      <ChatInput
        inputValue={inputValue}
        setInputValue={setInputValue}
        isThinking={isThinking}
        onStop={stop}
        onSubmit={handleSendMessage}
        placeholder={hasSite ? undefined : NO_SITE_PLACEHOLDER}
        threadSeed={threadSeed}
        contextSeedSent={contextSeedSent}
        pageAlarmContext={pageAlarmContext}
        onAttachContext={attachContext}
        onRemoveContext={removeContext}
      />
    </div>
  );
}

export default AskHeliosChat;
